
import React, { Fragment, useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Card,
  CardTitle,
  Badge,
  FormGroup,
  Input,
  Col,
  Row,
  UncontrolledTooltip,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { AGENCY_SERVICEREQUEST_LIST_URL } from '../../utils/APIEndpoints';
import { authHeader } from '../../utils/functions/CoreFunctions';
import API from '../../redux/actions/API';
import {
  amountFormat,
  getCurrentAgencyId,
  getAlertToast,
  dateTimeFormat,
  convertToUSDateTimeFormat,
} from '../../utils/functions/CustomFunctions';
import CustomViewButton from '../common/CustomViewButton';
import CustomTableLoader from '../common/CustomTableLoader';
import CustomTableEmptyRecord from '../common/CustomTableEmptyRecord';
import CustomTableHeader from '../common/CustomTableHeader';
import Pagination from '../common/Pagination';
import View from './view';
import { debounce } from 'underscore';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import Swal from 'sweetalert2';
import PaymentDetails from '../admin/serviceRequests/paymentDetails';
import { BsFillInfoCircleFill } from 'react-icons/bs';
import { CSVLink } from 'react-csv';
import CustomPageLoading from '../../utils/functions/CustomPageLoading';

const paymentStatusColor = {
  Success: 'primary',
  Failed: 'danger',
  Pending: 'warning',
};

//Table Columns
const tableColumns = [
  { title: 'Payment Date' },
  { title: 'Order ID' },
  { title: 'Customer Name' },
  { title: 'Document Name' },
  { title: 'Total Fee' },
  { title: 'Invoice' },
  { title: 'Document Type' },
  { title: 'Payment Type' },
  { title: 'Payment Status' },
  { title: 'Completed Form' },
  { title: 'Additional Documents' },
];

const headers = [
  { label: 'Payment Date', key: 'paymentDate' },
  { label: 'Order ID', key: 'orderId' },
  { label: 'Customer Name', key: 'name' },
  { label: 'Document Name', key: 'serviceName' },
  { label: 'Total Fee', key: 'amount' },
  { label: 'Invoice', key: 'invoiceId' },
  { label: 'Document Type', key: 'documentType' },
  { label: 'Payment Type', key: 'paymentType' },
  { label: 'Payment Status', key: 'paymentStatus' },
  { label: 'Completed Form', key: 'documentUrl' },
];

function Requests(props) {
  //State
  const [pageLoading, setPageLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [additionalDocuments, setAdditionalDocuments] = useState({});
  const viewToggle = () => setViewModal(!viewModal);
  const [dropdownOpen, setdropdownOpen] = useState(false);
  const [filter, setFilter] = useState({
    invoiceId: '',
    serviceName: '',
    status: '',
    paymentStartDate: '',
    paymentEndDate: '',
    paymentStatus: '',
    count: 10,
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [paymentDetailsModal, setPaymentDetailsModal] = useState(false);
  const [feeDetails, setFeeDetails] = useState({});
  const getDownloadFileName = () => {
    return 'Service_request-' + convertToUSDateTimeFormat(new Date());
  };
  const filterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };
  //Debounce
  const delayedQuery = useRef(
    debounce(
      (filter) =>
        getGridData({
          ...filter,
          offset: currentPage,
          agencyId: parseInt(getCurrentAgencyId()),
        }),
      500
    )
  ).current;

  const paymentDetailsToggle = () =>
    setPaymentDetailsModal(!paymentDetailsModal);

  //Onchange filter
  const changeFilterField = ({ target: { name, value } }) => {
    setFilter({ ...filter, [name]: value });
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setFilter((val) => ({
      ...val,
      invoiceId: '',
      serviceName: '',
      status: '',
      paymentStartDate: '',
      paymentEndDate: '',
      paymentStatus: '',
    }));
  };

  const onPageChanged = (page) => {
    setCurrentPage(page - 1);
    getGridData({
      ...filter,
      offset: page - 1,
      agencyId: parseInt(getCurrentAgencyId()),
    });
  };

  const startDateChange = (date) => {
    setStartDate(date || '');
    let paymentStartDate = date ? moment(date).format('MM/DD/YYYY') : '';
    setFilter({ ...filter, ['paymentStartDate']: paymentStartDate });
  };

  const endDateChange = (date) => {
    setEndDate(date || '');
    let paymentEndDate = date ? moment(date).format('MM/DD/YYYY') : '';
    setFilter({ ...filter, ['paymentEndDate']: paymentEndDate });
  };

  //update response values
  const updateDataValues = (resData) => {
    return resData.map((record) => {
      let { paymentDate, amount, firstName, lastName } = record;
      let formattedData = {
        paymentDate: paymentDate
          ? dateTimeFormat(paymentDate, 'MM/DD/YYYY hh:mm A')
          : '',
        amount: amountFormat(amount),
        name: firstName + ' ' + lastName,
      };
      let dataToReturn = { ...record, ...formattedData };
      return dataToReturn;
    });
  };

  //Get Grid Data
  const getGridData = async (queryParams) => {
    setLoading(true);
    const options = {
      method: 'GET',
      url: AGENCY_SERVICEREQUEST_LIST_URL,
      headers: authHeader(),
      params: queryParams,
    };
    try {
      const response = await API(options);
      const { status, data: initialResponse = [], totalCount } = response.data;
      if (status === 'Success') {
        setTotalRecords(totalCount);
        let dataToDis = updateDataValues(initialResponse);
        setGridData(dataToDis);
      }
    } catch (error) {
      if (error.response) {
        let { data } = error.response;
        Swal.fire(getAlertToast('error', data.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const viewAdditionalDocuments = async (documents, e) => {
    setAdditionalDocuments(JSON.parse(documents));
    setViewModal(true);
  };

  const viewFeeDetails = async (
    documentFee,
    additionalFee,
    totalFee,
    convenienceFee,
    e
  ) => {
    setFeeDetails({
      documentFee: documentFee,
      additionalFee: JSON.parse(additionalFee),
      totalFee: totalFee,
      convenienceFee: convenienceFee,
    });
    setPaymentDetailsModal(true);
  };

  const toggle = () => {
    setdropdownOpen(!dropdownOpen);
  };

  const handleRowPerPage = ({ target: { value, name } }) => {
    setCurrentPage(0);
    setFilter((va) => ({ ...va, [name]: value || undefined }));
  };

  useEffect(() => {
    delayedQuery(filter);
  }, [filter]);

  const { invoiceId, serviceName, status, paymentDate, paymentStatus } = filter;

  return (
    <Fragment>
      {pageLoading && <CustomPageLoading />}

      <Card body className="mt-5">
        <div>
          <CardTitle className="mb-4 f-l w-fit">Service Requests</CardTitle>
          <i
            className="fas fa-filter"
            style={{
              float: "right",
              margin: "10px 7px 3px 16px",
              fontSize: "19px",
              padding: "3px",
              cursor: "pointer",
            }}
            onClick={filterToggle}
          ></i>
          <div className="top-btn" style={{ float: "right" }}>
            <Dropdown isOpen={dropdownOpen} toggle={toggle}>
              <DropdownToggle color="danger" className="f-r" caret>
                Export
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem>
                  <CSVLink
                   className="link"
                    key={`csv_${gridData.length}`}
                    data={gridData}
                    headers={headers}
                    filename={`${getDownloadFileName()}.csv`}
                    target="_blank"
                  >
                    CSV
                  </CSVLink>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
        {isFilterOpen && (
          <Row form className="mb-4" style={{ marginTop: "1rem" }}>
            <Col md={3}>
              <FormGroup>
                <Input
                  type="text"
                  name="serviceName"
                  placeholder="Document Name"
                  onChange={changeFilterField}
                  value={serviceName}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Input
                  type="number"
                  name="invoiceId"
                  placeholder="Invoice"
                  onChange={changeFilterField}
                  value={invoiceId}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <DatePicker
                  style={{ zIndex: "9999 !important", width: "100%" }}
                  selected={startDate}
                  onChange={startDateChange}
                  dateFormat="MM/dd/yyy"
                  maxDate={new Date()}
                  placeholderText="Start Date"
                  className="form-control mb-3"
                  width="100%"
                  popperPlacement="bottom"
                  popperModifiers={{
                    flip: {
                      behavior: ["bottom"],
                    },
                    preventOverflow: {
                      enabled: false,
                    },
                    hide: {
                      enabled: false,
                    },
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <DatePicker
                  style={{ zIndex: "9999 !important", width: "100%" }}
                  selected={endDate}
                  onChange={endDateChange}
                  dateFormat="MM/dd/yyy"
                  placeholderText="End Date"
                  className="form-control mb-2"
                  minDate={startDate}
                  maxDate={new Date()}
                  // width='100%'
                  popperPlacement="bottom"
                  popperModifiers={{
                    flip: {
                      behavior: ["bottom"],
                    },
                    preventOverflow: {
                      enabled: false,
                    },
                    hide: {
                      enabled: false,
                    },
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={1} />
            <Col md={3}>
              <FormGroup>
                <Input
                  type="select"
                  name="paymentStatus"
                  placeholder="Status"
                  onChange={changeFilterField}
                  value={paymentStatus}
                >
                  <option value={""}>-- Show All Status --</option>
                  <option value={"Pending"}>Pending</option>
                  <option value={"Success"}>Success</option>
                  <option value={"Failed"}>Failed</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={2}>
              <div style={{ marginTop: "4px" }}>
                <Button color="danger" onClick={handleReset}>
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        )}
        <Table responsive hover>
          {/* Table Header */}
          <CustomTableHeader tableColumns={tableColumns} />
          {/* Table Header */}
          <tbody>
            {loading ? (
              // Table Loader
              <CustomTableLoader columnsCount={tableColumns.length} />
            ) : (
              // Table Loader
              <Fragment>
                {!gridData.length ? (
                  //  Empty Record
                  <CustomTableEmptyRecord columnsCount={tableColumns.length} />
                ) : (
                  //  Empty Record
                  <Fragment>
                    {gridData.map((record, key) => (
                      <tr key={`record_key_${key}`}>
                        <td style={{ width: "100px" }}>{record.paymentDate}</td>
                        <td>{record.orderId}</td>
                        <td>
                          {record.firstName} {record.lastName}
                        </td>
                        <td>{record.serviceName}</td>
                        <td>
                          {/* <Link
                            onClick={(e) =>
                              viewFeeDetails(
                                record.serviceAmount,
                                record.extraFeeList,
                                record.amount,
                                e
                              )
                            }
                          >
                            {record.amount}
                          </Link> */}

                          <div
                            style={{ color: "#007bff", cursor: "pointer" }}
                            onClick={(e) =>
                              viewFeeDetails(
                                record.serviceAmount,
                                record.extraFeeList,
                                record.amount,
                                record.serviceFeeAmount,
                                e
                              )
                            }
                          >
                            {/* {record.amount} */}
                            {(
                              (parseFloat(
                                String(record.serviceAmount).replace("$", "")
                              ) || 0) +
                              (parseFloat(
                                String(record.serviceFeeAmount).replace("$", "")
                              ) || 0) +
                              (record.extraFeeList
                                ? JSON.parse(record.extraFeeList).reduce(
                                    (acc, fee) =>
                                      acc + parseFloat(fee.totalAmount || 0),
                                    0
                                  )
                                : 0)
                            ).toFixed(2)}
                          </div>
                        </td>
                        <td>#{record.invoiceId}‎</td>
                        <td>{record.documentType}</td>
                        <td>{record.paymentType}</td>
                        <td>
                          <Badge
                            color={paymentStatusColor[record.paymentStatus]}
                          >
                            {record.paymentStatus}
                          </Badge>
                          {record.paymentStatus == "Failed" && (
                            <>
                              <span
                                style={{
                                  textDecoration: "underline",
                                  color: "blue",
                                  "margin-left": "5px",
                                }}
                                href="#"
                                id={`UncontrolledTooltipExample_${key}`}
                              >
                                <BsFillInfoCircleFill />
                              </span>

                              <UncontrolledTooltip
                                placement="top"
                                target={`UncontrolledTooltipExample_${key}`}
                              >
                                {record.responseText}
                              </UncontrolledTooltip>
                            </>
                          )}
                        </td>
                        <td style={{ width: "100px", textAlign: "center" }}>
                          {record.documentUrl ? (
                            <Button
                              tag="a"
                              href={record.documentUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="action-btn"
                              color="success"
                              type="button"
                            >
                              <span className="fas fa-download"></span>
                            </Button>
                          ) : (
                            ""
                          )}
                        </td>
                        <td style={{ width: "100px", textAlign: "center" }}>
                          {/* Custom View Button  */}
                          {record.additionalDocuments ? (
                            <CustomViewButton
                              keyvalue={key}
                              onClick={(e) =>
                                viewAdditionalDocuments(
                                  record.additionalDocuments,
                                  e
                                )
                              }
                            />
                          ) : (
                            ""
                          )}
                          {/* Custom View Button  */}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                )}
              </Fragment>
            )}
          </tbody>
          {/* Table Body */}
        </Table>
        {gridData.length ? (
          <div className="d-flex py-4 justify-content-end space_between">
            <div className="space_between">
              <div>
                <p className="per_page">Records/Page</p>
              </div>
              <div>
                <FormGroup>
                  <Input
                    className="pagi_drop"
                    type="select"
                    name="count"
                    onChange={handleRowPerPage}
                    value={filter.count}
                  >
                    <option value={""}>Show All</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Input>
                </FormGroup>
              </div>
            </div>
            {filter.count && (
              <div>
                <Pagination
                  itemClass="page-item"
                  linkClass="page-link"
                  activePage={currentPage + 1}
                  itemsCountPerPage={filter.count}
                  totalItemsCount={totalRecords}
                  onChange={onPageChanged}
                />
              </div>
            )}
          </div>
        ) : (
          ""
        )}
      </Card>
      {/* View Dialog */}
      {viewModal && (
        <View
          data={additionalDocuments}
          viewToggle={viewToggle}
          viewModal={viewModal}
        />
      )}
      {/* View Dialog */}
      {/* Payment Details Dialog */}
      {paymentDetailsModal && (
        <PaymentDetails
          feeDetails={feeDetails}
          paymentDetailsToggle={paymentDetailsToggle}
          paymentDetailsModal={paymentDetailsModal}
        />
      )}
      {/* Payment Details Dialog */}
    </Fragment>
  );
}

export default Requests;
